<template>
  <section class="signup-page min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden">
    <!-- Background -->
    <div class="absolute inset-0 bg-neutral-950"></div>
    <div class="signup-bg-glow"></div>

    <div class="w-full max-w-sm relative z-10">
      <!-- Branding -->
      <div class="text-center mb-8 signup-entrance" style="animation-delay: 0s">
        <div class="flex justify-center mb-5">
          <div class="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center">
            <img src="@/assets/globe.png" class="h-9 w-9" alt="Neutral Fuels" />
          </div>
        </div>
        <div class="flex items-center justify-center gap-2 mb-2">
          <h1 class="text-xl font-bold text-white tracking-tight">Create Account</h1>
        </div>
        <p class="text-sm text-neutral-400">Join Neutral Fuels OMS</p>
      </div>

      <!-- Card -->
      <div class="signup-entrance bg-neutral-900 border border-neutral-800 rounded-2xl p-7 shadow-2xl" style="animation-delay: 0.1s">
        <form @submit.prevent="handleSubmit" class="space-y-4">
          <div>
            <label for="displayName" class="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Full Name</label>
            <input
              id="displayName"
              v-model="displayName"
              type="text"
              autocomplete="name"
              placeholder="Your full name"
              required
              autofocus
              class="w-full px-4 py-2.5 bg-neutral-800 border border-neutral-700 rounded-xl text-base text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-200"
            />
          </div>

          <div>
            <label for="email" class="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Email</label>
            <input
              id="email"
              v-model="email"
              type="email"
              autocomplete="email"
              placeholder="you@neutralfuels.com"
              required
              class="w-full px-4 py-2.5 bg-neutral-800 border border-neutral-700 rounded-xl text-base text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-200"
            />
          </div>

          <div>
            <label for="password" class="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Password</label>
            <div class="relative">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="new-password"
                minlength="6"
                placeholder="Min 6 characters"
                required
                class="w-full px-4 py-2.5 pr-11 bg-neutral-800 border border-neutral-700 rounded-xl text-base text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-200"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                class="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-neutral-500 hover:text-neutral-300 transition-colors"
              >
                <span class="material-icons text-[20px]" aria-hidden="true">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label for="country" class="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Country</label>
              <select
                v-if="countries"
                id="country"
                v-model="selectedCoutnryId"
                required
                class="w-full px-3 py-2.5 bg-neutral-800 border border-neutral-700 rounded-xl text-base text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-200 cursor-pointer"
              >
                <option :value="null" disabled class="text-neutral-500">Select</option>
                <option :value="country.id" v-for="country in countries" :key="country.id">
                  {{ country.countryname }}
                </option>
              </select>
              <select
                v-else
                id="country"
                disabled
                aria-label="Country (loading)"
                class="w-full px-3 py-2.5 bg-neutral-800 border border-neutral-700 rounded-xl text-base text-neutral-500 opacity-60"
              >
                <option>Loading…</option>
              </select>
            </div>

            <div>
              <label for="site" class="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Site</label>
              <select
                v-if="sites"
                id="site"
                v-model="selectedSiteId"
                required
                class="w-full px-3 py-2.5 bg-neutral-800 border border-neutral-700 rounded-xl text-base text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-200 cursor-pointer"
              >
                <option :value="null" disabled class="text-neutral-500">Select</option>
                <option :value="{ siteId: site.id, siteName: site.sitename }" v-for="site in sites" :key="site.id">
                  {{ site.sitename }}
                </option>
              </select>
              <select
                v-else
                id="site"
                disabled
                aria-label="Site (loading)"
                class="w-full px-3 py-2.5 bg-neutral-800 border border-neutral-700 rounded-xl text-base text-neutral-500 opacity-60"
              >
                <option>Loading…</option>
              </select>
            </div>
          </div>

          <!-- Field validation error -->
          <p v-if="fieldError" role="alert" class="text-xs text-red-400">{{ fieldError }}</p>

          <!-- Error -->
          <div v-if="error" role="alert" class="flex items-center gap-2 px-3 py-2.5 bg-red-500/10 border border-red-500/20 rounded-xl">
            <span class="material-icons text-red-400 text-sm" aria-hidden="true">error</span>
            <p class="text-xs text-red-400">{{ error }}</p>
          </div>

          <button
            type="submit"
            :disabled="isPending || !countries || !sites"
            class="w-full px-4 py-2.5 bg-primary-700 text-white rounded-xl hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-neutral-900 transition-all duration-200 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <svg v-if="isPending" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isPending ? 'Creating account...' : 'Create Account' }}
          </button>
        </form>

        <div class="mt-5 pt-5 border-t border-neutral-800 text-center">
          <p class="text-xs text-neutral-400">
            Already have an account?
            <router-link :to="{ name: 'Login', query: $route.query }" class="text-primary-400 hover:text-primary-300 font-semibold transition-colors ml-1">
              Sign in
            </router-link>
          </p>
        </div>
      </div>

      <!-- Footer -->
      <p class="signup-entrance text-center text-[11px] text-neutral-500 mt-8" style="animation-delay: 0.2s">
        &copy; {{ currentYear }} Neutral Fuels Holdings Ltd.
      </p>
    </div>
  </section>
</template>

<script>
import { ref, computed } from '@vue/reactivity'
import { useRouter } from 'vue-router'
import getCollection from '@/composables/getCollection'
import { useStore } from 'vuex'

// Map Firebase auth error codes to human messages — raw err.message strings
// like "Firebase: Error (auth/weak-password)." must not reach users.
const AUTH_ERROR_MESSAGES = {
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/weak-password': 'Password must be at least 6 characters.',
  'auth/too-many-requests': 'Too many attempts — please try again in a few minutes.',
  'auth/network-request-failed': 'Network error — check your connection and try again.',
}

const friendlyAuthError = (err) =>
  AUTH_ERROR_MESSAGES[err?.code] || 'Something went wrong — please try again.'

export default {
  setup() {
    const email = ref('')
    const password = ref('')
    const displayName = ref('')
    const selectedCoutnryId = ref(null)
    const selectedSiteId = ref(null)
    const error = ref(null)
    const fieldError = ref(null)
    const isPending = ref(false)
    const showPassword = ref(false)

    const router = useRouter()
    const store = useStore()

    const { documents: countries } = getCollection('countries')
    const { documents: sites } = getCollection('sites')

    const currentYear = computed(() => new Date().getFullYear())

    const handleSubmit = async () => {
      error.value = null
      fieldError.value = null

      // Validate BEFORE dispatching — the signup action creates the Firebase
      // Auth account first, so a null site would orphan the account mid-flow.
      if (!selectedCoutnryId.value || !selectedSiteId.value) {
        fieldError.value = 'Please select your country and site.'
        return
      }
      if (password.value.length < 6) {
        fieldError.value = 'Password must be at least 6 characters.'
        return
      }

      try {
        isPending.value = true
        await store.dispatch('signup', {
          email: email.value,
          password: password.value,
          displayName: displayName.value,
          countryId: selectedCoutnryId.value,
          siteId: selectedSiteId.value,
        })
        const redirectPath = router.currentRoute.value.query.redirect || '/dashboard/operations/orders'
        router.push(redirectPath)
      } catch (err) {
        isPending.value = false
        error.value = friendlyAuthError(err)
      }
    }

    return {
      email, password, displayName, selectedSiteId, selectedCoutnryId,
      handleSubmit, error, fieldError, isPending, countries, sites, currentYear,
      showPassword,
    }
  },
}
</script>

<style scoped>
.signup-bg-glow {
  position: absolute;
  top: -40%;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(30, 144, 255, 0.1) 0%, transparent 70%);
  pointer-events: none;
}

.signup-entrance {
  animation: signupFade 0.5s cubic-bezier(0.4, 0, 0.2, 1) both;
}

@media (prefers-reduced-motion: reduce) {
  .signup-entrance {
    animation: none;
  }
}

@keyframes signupFade {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
